import React, { useEffect, useRef, useState } from 'react';
import { Check, Trash2, Pin, Plus, ListTodo } from 'lucide-react';
import { stickyStyle } from '../utils/stickyColors';
import {
  parseStickyContent,
  serializeStickyContent,
  makeTodoItem,
  emptyStickyContent,
} from '../utils/stickyContent';

const MIN_W = 180;
const MAX_W = 480;
const MIN_H = 160;
const MAX_H = 520;

const StickyNoteCard = ({
  note,
  onChange,
  onDelete,
  onToggleDone,
  onToggleOverlay,
  onResizeEnd,
  draggable = false,
  onDragEnd,
  resizable = true,
  className = '',
}) => {
  const colors = stickyStyle(note.color);
  const parsed = parseStickyContent(note.body || '');
  const [structured, setStructured] = useState(parsed.structured || parsed.items.length > 0);
  const [title, setTitle] = useState(parsed.title || '');
  const [items, setItems] = useState(parsed.items || []);
  const [notes, setNotes] = useState(parsed.notes || '');
  const [plainBody, setPlainBody] = useState(parsed.structured ? '' : note.body || '');
  const [size, setSize] = useState({
    width: note.width || 260,
    height: note.height || 280,
  });
  const saveTimer = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const nodeRef = useRef(null);
  const [livePos, setLivePos] = useState(null);

  useEffect(() => {
    const next = parseStickyContent(note.body || '');
    const isStruct = next.structured || next.items.length > 0;
    setStructured(isStruct);
    setTitle(next.title || '');
    setItems(next.items || []);
    setNotes(next.notes || '');
    setPlainBody(isStruct ? '' : note.body || '');
  }, [note.id, note.body]);

  useEffect(() => {
    setSize({
      width: note.width || 260,
      height: note.height || 280,
    });
  }, [note.id, note.width, note.height]);

  // Live-Drag-Position freigeben, sobald Props nachgezogen sind
  useEffect(() => {
    if (!livePos) return;
    const x = Number(note.posX);
    const y = Number(note.posY);
    if (
      Number.isFinite(x) &&
      Number.isFinite(y) &&
      Math.abs(x - livePos.x) < 0.05 &&
      Math.abs(y - livePos.y) < 0.05
    ) {
      setLivePos(null);
    }
  }, [note.posX, note.posY, livePos]);

  useEffect(() => () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
  }, []);

  const queueSave = (payload) => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      onChange?.(payload);
    }, 400);
  };

  const saveStructured = (next) => {
    const content = {
      title: next.title ?? title,
      items: next.items ?? items,
      notes: next.notes ?? notes,
    };
    queueSave({ body: serializeStickyContent(content) });
  };

  const enableChecklist = () => {
    const base = plainBody.trim()
      ? { title: plainBody.split('\n')[0].slice(0, 80), items: [makeTodoItem('')], notes: plainBody.split('\n').slice(1).join('\n') }
      : { ...emptyStickyContent(), title: 'Tasks', items: [makeTodoItem(''), makeTodoItem('')] };
    setStructured(true);
    setTitle(base.title);
    setItems(base.items);
    setNotes(base.notes || '');
    setPlainBody('');
    queueSave({ body: serializeStickyContent(base) });
  };

  const toggleItem = (id) => {
    const nextItems = items.map((it) =>
      it.id === id ? { ...it, done: !it.done } : it
    );
    setItems(nextItems);
    saveStructured({ items: nextItems });
  };

  const updateItemText = (id, text) => {
    const nextItems = items.map((it) => (it.id === id ? { ...it, text } : it));
    setItems(nextItems);
    saveStructured({ items: nextItems });
  };

  const removeItem = (id) => {
    const nextItems = items.filter((it) => it.id !== id);
    setItems(nextItems);
    saveStructured({ items: nextItems });
  };

  const addItem = (afterId = null) => {
    const newItem = makeTodoItem('');
    let nextItems;
    if (afterId) {
      const idx = items.findIndex((it) => it.id === afterId);
      nextItems =
        idx >= 0
          ? [...items.slice(0, idx + 1), newItem, ...items.slice(idx + 1)]
          : [...items, newItem];
    } else {
      nextItems = [...items, newItem];
    }
    setItems(nextItems);
    saveStructured({ items: nextItems });
    requestAnimationFrame(() => {
      const el = nodeRef.current?.querySelector(`input[data-todo-id="${newItem.id}"]`);
      el?.focus();
    });
  };

  const onTodoKeyDown = (e, itemId) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    addItem(itemId);
  };

  const onPointerDown = (e) => {
    if (!draggable || e.button !== 0) return;
    if (e.target.closest('textarea,button,input,.sticky-note__resize,.sticky-note__todo')) return;
    const el = nodeRef.current;
    if (!el) return;
    e.preventDefault();
    const parent = el.offsetParent || document.body;
    const parentRect = parent.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: Number(note.posX) || 0,
      origY: Number(note.posY) || 0,
      parentW: parentRect.width,
      parentH: parentRect.height,
    };
    el.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (resizeState.current) {
      const r = resizeState.current;
      const nextW = Math.max(MIN_W, Math.min(MAX_W, r.origW + (e.clientX - r.startX)));
      const nextH = Math.max(MIN_H, Math.min(MAX_H, r.origH + (e.clientY - r.startY)));
      setSize({ width: nextW, height: nextH });
      r.nextW = nextW;
      r.nextH = nextH;
      return;
    }
    if (!dragState.current || !nodeRef.current) return;
    const d = dragState.current;
    const dx = ((e.clientX - d.startX) / d.parentW) * 100;
    const dy = ((e.clientY - d.startY) / d.parentH) * 100;
    const noteW = nodeRef.current.offsetWidth || 260;
    const noteH = nodeRef.current.offsetHeight || 280;
    const maxX = Math.max(0, ((d.parentW - noteW) / d.parentW) * 100);
    const maxY = Math.max(0, ((d.parentH - noteH) / d.parentH) * 100);
    const nextX = Math.max(0, Math.min(maxX, d.origX + dx));
    const nextY = Math.max(0, Math.min(maxY, d.origY + dy));
    nodeRef.current.style.left = `${nextX}%`;
    nodeRef.current.style.top = `${nextY}%`;
    dragState.current.nextX = nextX;
    dragState.current.nextY = nextY;
  };

  const onPointerUp = () => {
    if (resizeState.current) {
      const { nextW, nextH, origW, origH } = resizeState.current;
      resizeState.current = null;
      if (nextW == null || nextH == null) return;
      if (Math.abs(nextW - origW) < 2 && Math.abs(nextH - origH) < 2) return;
      onResizeEnd?.({ width: Math.round(nextW), height: Math.round(nextH) });
      return;
    }
    if (!dragState.current) return;
    const { nextX, nextY, origX, origY } = dragState.current;
    dragState.current = null;
    if (nextX == null || nextY == null) return;
    if (Math.abs(nextX - origX) < 0.2 && Math.abs(nextY - origY) < 0.2) return;
    setLivePos({ x: nextX, y: nextY });
    onDragEnd?.({ posX: nextX, posY: nextY });
  };

  const onResizePointerDown = (e) => {
    if (!resizable || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origW: size.width,
      origH: size.height,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const doneCount = items.filter((it) => it.done).length;

  return (
    <div
      ref={nodeRef}
      className={`sticky-note ${note.done ? 'sticky-note--done' : ''} ${className}`}
      style={{
        '--sticky-bg': colors.bg,
        '--sticky-ink': colors.ink,
        backgroundColor: colors.bg,
        color: colors.ink,
        width: size.width,
        height: size.height,
        transform: `rotate(${note.rotation || 0}deg)`,
        ...(draggable
          ? {
              left: `${livePos?.x ?? note.posX ?? 8}%`,
              top: `${livePos?.y ?? note.posY ?? 12}%`,
            }
          : {}),
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="sticky-note__curl" aria-hidden />
      <div className="sticky-note__tape" style={{ background: colors.tape }} />
      <div className="sticky-note__toolbar">
        {!structured ? (
          <button
            type="button"
            title="Checkliste aktivieren"
            onClick={enableChecklist}
            className="sticky-note__icon-btn"
          >
            <ListTodo className="w-3.5 h-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          title={note.done ? 'Als offen markieren' : 'Als erledigt markieren'}
          onClick={() => onToggleDone?.(!note.done)}
          className="sticky-note__icon-btn"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        {onToggleOverlay ? (
          <button
            type="button"
            title={
              note.showOnOverlay
                ? 'Loslösen (auf anderen Seiten ausblenden)'
                : 'Anheften — auf allen Seiten anzeigen (wenn Overlay an)'
            }
            onClick={() => onToggleOverlay?.(!note.showOnOverlay)}
            className={`sticky-note__icon-btn ${note.showOnOverlay ? 'is-active' : ''}`}
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          title="Löschen"
          onClick={() => onDelete?.()}
          className="sticky-note__icon-btn sticky-note__icon-btn--danger"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {structured ? (
        <div className="sticky-note__content">
          <input
            type="text"
            className="sticky-note__title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              saveStructured({ title: e.target.value });
            }}
            placeholder="Title…"
          />
          <div className="sticky-note__todos">
            {items.map((item) => (
              <div key={item.id} className={`sticky-note__todo ${item.done ? 'is-done' : ''}`}>
                <button
                  type="button"
                  className={`sticky-note__check ${item.done ? 'is-checked' : ''}`}
                  onClick={() => toggleItem(item.id)}
                  title={item.done ? 'Offen' : 'Erledigt'}
                  aria-pressed={item.done}
                >
                  {item.done ? <Check className="w-3 h-3" strokeWidth={3} /> : null}
                </button>
                <input
                  type="text"
                  className="sticky-note__todo-input"
                  data-todo-id={item.id}
                  value={item.text}
                  onChange={(e) => updateItemText(item.id, e.target.value)}
                  onKeyDown={(e) => onTodoKeyDown(e, item.id)}
                  placeholder="Task…"
                />
                <button
                  type="button"
                  className="sticky-note__todo-remove"
                  onClick={() => removeItem(item.id)}
                  title="Punkt entfernen"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button type="button" className="sticky-note__add-todo" onClick={addItem}>
              <Plus className="w-3.5 h-3.5" />
              Punkt
              {items.length > 0 ? (
                <span className="sticky-note__todo-progress">
                  {doneCount}/{items.length}
                </span>
              ) : null}
            </button>
          </div>
          <textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              saveStructured({ notes: e.target.value });
            }}
            placeholder="Notizen…"
            className="sticky-note__notes"
            spellCheck={false}
          />
        </div>
      ) : (
        <textarea
          value={plainBody}
          onChange={(e) => {
            setPlainBody(e.target.value);
            queueSave({ body: e.target.value });
          }}
          placeholder="Notiz…"
          className="sticky-note__body"
          spellCheck={false}
        />
      )}

      {resizable ? (
        <span
          className="sticky-note__resize"
          title="Größe anpassen"
          onPointerDown={onResizePointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      ) : null}
    </div>
  );
};

export default StickyNoteCard;
